<template>
  <div data-testid="dropdown" v-if="width !== ''" class="" style="background: white;">
    <v-combobox
      data-testid="dropdown__combobox"
      v-if="customvalue"
      ref="element"
      style="background: white;"
      outlined
      dense
      :style="`width:${width}`"
      :label="label ?? ''"
      :clearable="clearable"
      :error-messages="error"
      v-model="content"
      :search-input.sync="searchInput"
      @click:clear="onClear"
      @change="updateValue()"
      @input="onInternalInput"
      :items="items"
      :chips="chips"
      :deletable-chips="chips"
      :hide-details="hidedetails"
      :disabled="disabled"
      :item-text="effectiveItemText"
      :item-value="effectiveItemValue"
      :readonly="readonly"
      :multiple="multiple"
      :prepend-inner-icon="prependinnericon"
      :append-icon="resolvedAppendIcon"
      :prefix="prefix"
      :suffix="suffix"
      :class="`${transformClass} ${roundedClass}`"
      :menu-props="menuProps"
    >
      <template v-if="itemClass" v-slot:item="data">
        <span data-testid="dropdown__combobox-item" :class="itemClass">
          <template v-if="isInactive(data.item)">
            {{ (typeof data.item === 'object' ? data.item[itemtext] : data.item).replace(' (Inactive)', '') }}
            <span data-testid="dropdown__combobox-item-inactive" class="error--text"> (Inactive)</span>
          </template>
          <template v-else>
            {{ typeof data.item === 'object' ? data.item[itemtext] : data.item }}
          </template>
        </span>
      </template>
    </v-combobox>
    <v-autocomplete
      data-testid="dropdown__autocomplete"
      ref="element"
      outlined
      dense
      :style="`width:${width}`"
      :label="label ?? ''"
      :clearable="clearable"
      :error-messages="error"
      v-model="content"
      :search-input.sync="searchInput"
      @click:clear="onClear"
      @change="updateValue()"
      @input="onInternalInput"
      :items="resolvedItems"
      :filter="menuSearch ? menuFilter : undefined"
      :chips="chips"
      :deletable-chips="chips"
      :hide-details="hidedetails"
      :disabled="disabled"
      :item-text="effectiveItemText"
      :item-value="effectiveItemValue"
      :readonly="readonly"
      :multiple="multiple"
      :prepend-inner-icon="prependinnericon"
      :append-icon="resolvedAppendIcon"
      :prefix="prefix"
      :suffix="suffix"
      :placeholder="placeholder"
      :class="[`${transformClass}`, roundedClass, { 'dropdown--menu-search': menuSearch }]"
      :menu-props="menuProps"
      class="autocomplete-dropdown"
    >
      <template v-if="menuSearch" v-slot:prepend-item>
        <div
          class="lb-dropdown__menu-search"
          data-testid="dropdown__menu-search"
          @click.stop
          @mousedown.stop
        >
        <v-text-field
          ref="menuSearchInput"
          v-model="menuSearchQuery"
          dense
          outlined
          hide-details
          :placeholder="searchPlaceholder"
          data-testid="dropdown__menu-search-input"
          @click.stop
          @mousedown.stop
          @keydown.native.stop
          @keyup.native.stop
        >
          <template v-slot:append>
            <img
              src="/img/icons/fbc-icons/SearchIcon.svg"
              alt=""
              aria-hidden="true"
              class="lb-dropdown__menu-search-icon"
              data-testid="dropdown__menu-search-icon"
            />
          </template>
        </v-text-field>
        </div>
        <v-divider class="lb-dropdown__menu-divider" />
      </template>

      <template v-if="menuSearch" v-slot:item="{ item, on, attrs }">
        <v-list-item
          v-bind="attrs"
          class="lb-dropdown__menu-item"
          data-testid="dropdown__menu-item"
          v-on="on"
          @click="onMenuSearchItemClick"
        >
          <v-list-item-action v-if="multiple" class="ma-0 mr-2 flex-shrink-0">
            <v-simple-checkbox :value="attrs.inputValue" color="primary" tabindex="-1" />
          </v-list-item-action>
          <v-list-item-content class="py-0">
            <v-list-item-title class="lb-dropdown__menu-item-title">
              <template v-if="isInactive(item)">
                {{ getItemLabel(item).replace(' (Inactive)', '') }}
                <span class="error--text"> (Inactive)</span>
              </template>
              <template v-else>
                {{ getItemLabel(item) }}
              </template>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <template v-else-if="itemClass" v-slot:item="data">
        <span data-testid="dropdown__autocomplete-item" :class="itemClass">
          <template v-if="isInactive(data.item)">
            {{ (typeof data.item === 'object' ? data.item[itemtext] : data.item).replace(' (Inactive)', '') }}
            <span data-testid="dropdown__autocomplete-item-inactive" class="error--text"> (Inactive)</span>
          </template>
          <template v-else>
            {{ typeof data.item === 'object' ? data.item[itemtext] : data.item }}
          </template>
        </span>
      </template>

      <template v-if="chips && multiple" v-slot:selection="{ attrs, item, parent, selected }">
        <v-chip
          v-bind="attrs"
          :input-value="selected"
          class="lb-dropdown__selected-chip pa-0 px-1 ma-0 noTransform"
          data-testid="dropdown__selected-chip"
        >
          <span class="lb-dropdown__selected-chip-label" data-testid="dropdown__selected-item">
            {{ getItemLabel(item) }}
          </span>
          <v-icon
            class="lb-dropdown__selected-chip-close pa-0"
            small
            @click.stop="parent.selectItem(item)"
            data-testid="dropdown__selected-item-close-icon"
          >mdi-close</v-icon>
        </v-chip>
      </template>
    </v-autocomplete>
  </div>
  <v-combobox
    data-testid="dropdown__combobox"
    ref="element"
    outlined
    dense
    style="background: white;"
    v-else-if="customvalue"
    :label="label ?? ''"
    :clearable="clearable"
    :error-messages="error"
    v-model="content"
    :search-input.sync="searchInput"
    @click:clear="onClear"
    @change="updateValue()"
    @input="onInternalInput"
    :items="items"
    :chips="chips"
    :deletable-chips="chips"
    :hide-details="hidedetails"
    :disabled="disabled"
    :item-text="effectiveItemText"
    :item-value="effectiveItemValue"
    :readonly="readonly"
    :multiple="multiple"
    :prepend-inner-icon="prependinnericon"
    :append-icon="resolvedAppendIcon"
    :prefix="prefix"
    :suffix="suffix"
    :placeholder="placeholder"
    :class="`${transformClass} ${roundedClass}`"
    :menu-props="menuProps"
    class="autocomplete-dropdown"
  >
    <template v-if="itemClass" v-slot:item="data">
        <span data-testid="dropdown__combobox-item" :class="itemClass">
          <template v-if="isInactive(data.item)">
            {{ (typeof data.item === 'object' ? data.item[itemtext] : data.item).replace(' (Inactive)', '') }}
            <span data-testid="dropdown__combobox-item-inactive" class="error--text"> (Inactive)</span>
          </template>
          <template v-else>
            {{ typeof data.item === 'object' ? data.item[itemtext] : data.item }}
          </template>
        </span>
      </template>
  </v-combobox>
  <v-select
    v-else-if="!searchable"
    data-testid="dropdown__select"
    ref="element"
    outlined
    dense
    :label="label ?? ''"
    :clearable="clearable"
    :error-messages="error"
    v-model="content"
    @click:clear="onClear"
    @change="updateValue()"
    @input="onInternalInput"
    :items="items"
    :chips="chips"
    :deletable-chips="chips"
    :hide-details="hidedetails"
    :disabled="disabled"
    :item-text="effectiveItemText"
    :item-value="effectiveItemValue"
    item-disabled="disabled"
    :readonly="readonly"
    :multiple="multiple"
    :prepend-inner-icon="prependinnericon"
    :append-icon="resolvedAppendIcon"
    :prefix="prefix"
    :suffix="suffix"
    :placeholder="placeholder"
    :class="`${transformClass} ${roundedClass}`"
    :menu-props="menuProps"
    class="autocomplete-dropdown"
  >
    <template v-if="itemClass" v-slot:item="data">
      <span data-testid="dropdown__select-item" :class="itemClass">
        <template v-if="isInactive(data.item)">
          {{ (typeof data.item === 'object' ? data.item[itemtext] : data.item).replace(' (Inactive)', '') }}
          <span class="error--text"> (Inactive)</span>
        </template>
        <template v-else>
          {{ typeof data.item === 'object' ? data.item[itemtext] : data.item }}
        </template>
      </span>
    </template>
  </v-select>
  <v-select
    v-else-if="menuSearch && !multiple"
    data-testid="dropdown__autocomplete"
    ref="element"
    outlined
    dense
    :label="label ?? ''"
    :clearable="clearable"
    :error-messages="error"
    v-model="content"
    @click:clear="onClear"
    @change="updateValue()"
    @input="onInternalInput"
    :items="items"
    :hide-details="hidedetails"
    :disabled="disabled"
    :item-text="effectiveItemText"
    :item-value="effectiveItemValue"
    :readonly="readonly"
    :prepend-inner-icon="prependinnericon"
    :append-icon="resolvedAppendIcon"
    :prefix="prefix"
    :suffix="suffix"
    :placeholder="placeholder"
    :class="[`${transformClass}`, roundedClass, 'dropdown--menu-search']"
    :menu-props="menuProps"
    class="autocomplete-dropdown"
  >
    <template v-slot:prepend-item>
      <div
        class="lb-dropdown__menu-search"
        data-testid="dropdown__menu-search"
        @click.stop
        @mousedown.stop
      >
        <v-text-field
          ref="menuSearchInput"
          v-model="menuSearchQuery"
          dense
          outlined
          hide-details
          :placeholder="searchPlaceholder"
          data-testid="dropdown__menu-search-input"
          @click.stop
          @mousedown.stop
          @keydown.native.stop
          @keyup.native.stop
        >
          <template v-slot:append>
            <img
              src="/img/icons/fbc-icons/SearchIcon.svg"
              alt=""
              aria-hidden="true"
              class="lb-dropdown__menu-search-icon"
              data-testid="dropdown__menu-search-icon"
            />
          </template>
        </v-text-field>
      </div>
      <v-divider class="lb-dropdown__menu-divider" />
    </template>

    <template v-slot:item="{ item, on, attrs }">
      <v-list-item
        v-show="itemMatchesSearch(item)"
        v-bind="attrs"
        class="lb-dropdown__menu-item"
        data-testid="dropdown__menu-item"
        v-on="on"
        @click="onMenuSearchItemClick"
      >
        <v-list-item-content class="py-0">
          <v-list-item-title class="lb-dropdown__menu-item-title">
            <template v-if="isInactive(item)">
              {{ getItemLabel(item).replace(' (Inactive)', '') }}
              <span class="error--text"> (Inactive)</span>
            </template>
            <template v-else>
              {{ getItemLabel(item) }}
            </template>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>
  </v-select>
  <v-autocomplete
    data-testid="dropdown__autocomplete"
    ref="element"
    outlined
    dense
    v-else
    :label="label ?? ''"
    :clearable="clearable"
    :error-messages="error"
    v-model="content"
    :search-input.sync="searchInput"
    @click:clear="onClear"
    @change="updateValue()"
    @input="onInternalInput"
    :items="resolvedItems"
    :filter="menuSearch ? menuFilter : undefined"
    :chips="chips"
    :deletable-chips="chips"
    :hide-details="hidedetails"
    :disabled="disabled"
    :item-text="effectiveItemText"
    :item-value="effectiveItemValue"
    :readonly="readonly"
    :multiple="multiple"
    :prepend-inner-icon="prependinnericon"
    :append-icon="resolvedAppendIcon"
    :prefix="prefix"
    :suffix="suffix"
    :placeholder="placeholder"
    :class="[`${transformClass}`, roundedClass, { 'dropdown--menu-search': menuSearch }]"
    :menu-props="menuProps"
    class="autocomplete-dropdown"
  >
    <template v-if="menuSearch" v-slot:prepend-item>
      <div
        class="lb-dropdown__menu-search"
        data-testid="dropdown__menu-search"
        @click.stop
        @mousedown.stop
      >
        <v-text-field
          ref="menuSearchInput"
          v-model="menuSearchQuery"
          dense
          outlined
          hide-details
          :placeholder="searchPlaceholder"
          data-testid="dropdown__menu-search-input"
          @click.stop
          @mousedown.stop
          @keydown.native.stop
          @keyup.native.stop
        >
          <template v-slot:append>
            <img
              src="/img/icons/fbc-icons/SearchIcon.svg"
              alt=""
              aria-hidden="true"
              class="lb-dropdown__menu-search-icon"
              data-testid="dropdown__menu-search-icon"
            />
          </template>
        </v-text-field>
      </div>
      <v-divider class="lb-dropdown__menu-divider" />
    </template>

    <template v-if="menuSearch" v-slot:item="{ item, on, attrs }">
      <v-list-item
        v-bind="attrs"
        class="lb-dropdown__menu-item"
        data-testid="dropdown__menu-item"
        v-on="on"
        @click="onMenuSearchItemClick"
      >
        <v-list-item-action v-if="multiple" class="ma-0 mr-2 flex-shrink-0">
          <v-simple-checkbox :value="attrs.inputValue" color="primary" tabindex="-1" />
        </v-list-item-action>
        <v-list-item-content class="py-0">
          <v-list-item-title class="lb-dropdown__menu-item-title">
            <template v-if="isInactive(item)">
              {{ getItemLabel(item).replace(' (Inactive)', '') }}
              <span class="error--text"> (Inactive)</span>
            </template>
            <template v-else>
              {{ getItemLabel(item) }}
            </template>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>

    <template v-else-if="itemClass" v-slot:item="data">
        <span data-testid="dropdown__autocomplete-item" :class="itemClass">
          <template v-if="isInactive(data.item)">
            {{ (typeof data.item === 'object' ? data.item[itemtext] : data.item).replace(' (Inactive)', '') }}
            <span data-testid="dropdown__autocomplete-item-inactive" class="error--text"> (Inactive)</span>
          </template>
          <template v-else>
            {{ typeof data.item === 'object' ? data.item[itemtext] : data.item }}
          </template>
        </span>
      </template>

    <template v-if="chips && multiple" v-slot:selection="{ attrs, item, parent, selected }">
      <v-chip
        v-bind="attrs"
        :input-value="selected"
        class="lb-dropdown__selected-chip pa-0 px-1 ma-0 noTransform"
        data-testid="dropdown__selected-chip"
      >
        <span class="lb-dropdown__selected-chip-label" data-testid="dropdown__selected-item">
          {{ getItemLabel(item) }}
        </span>
        <v-icon
          class="lb-dropdown__selected-chip-close pa-0"
          small
          @click.stop="parent.selectItem(item)"
          data-testid="dropdown__selected-item-close-icon"
        >mdi-close</v-icon>
      </v-chip>
    </template>
  </v-autocomplete>
</template>

<script>
import {
  DIALOG_MENU_Z_INDEX,
  positionMenuNearActivator,
} from '@/utils/sync-attached-menu';

export default {
  activated() {
    this.setValue();
    this.updateValue();
  },
  created() {
    this.setValue();
    this.updateValue();
  },
  mounted() {
    this.$nextTick(() => {
      this.bindScrollClose();
      this.bindMenuCloseReset();
    });
  },
  updated() {
    this.$nextTick(() => {
      this.bindScrollClose();
      this.bindMenuCloseReset();
    });
  },
  data: () => ({
    content: "",
    tempContent: "",
    searchInput: null,
    closeMenuTimer: null,
    scrollCloseBinding: null,
    menuSearchQuery: "",
    menuCloseUnwatch: null,
    menuClosing: false,
    menuPinTimer: null,
    ignoreScrollClose: false,
    scrollIgnoreTimer: null,
  }),
  props: {
    error: {
      type: [String, Array],
    },
    label: {
      type: String,
    },
    value: {
      type: [String, Array, Number],
      default: function () {
        return [];
      },
    },
    items: {
      type: Array,
      default: function () {
        return [];
      },
    },
    width: {
      type: String,
      default: "",
    },
    itemtext: {
      type: String,
      default: "name",
    },
    itemvalue: {
      type: String,
      default: "value",
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    customvalue: {
      type: Boolean,
      default: false,
    },
    /**
     * When false, renders v-select instead of v-autocomplete.
     * Use inside dialogs where autocomplete menus fail to close after pick.
     * Default true keeps existing searchable behaviour everywhere else.
     */
    searchable: {
      type: Boolean,
      default: true,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    hidedetails: {
      type: Boolean,
      default: false,
    },
    chips: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    prependinnericon: {
      type: String,
      default: "",
    },
    appendicon: {
      type: String,
      default: "",
    },
    /**
     * Alias used across the app (same pattern as lb-string).
     * Prefer this when provided; falls back to `appendicon`.
     * Empty string keeps prior behaviour (no forced icon).
     */
    appendiconinner: {
      type: String,
      default: "",
    },
    prefix: {
      type: String,
      default: "",
    },
    suffix: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder:{
      type:String,
      default:''
    },
    itemClass: {
      type: String,
      default: ''
    },
    roundedClass: {
      type: String,
      default: "",
    },
    transformClass: {
      type: String,
      default: "noTransform",
    },
    /**
      * Controls where the underlying Vuetify menu content is mounted.
      * - true: attach to document.body
      * - false: render in-place near the field
      * - string: CSS selector to attach to
      * Default false — callers consistently use in-place menus (dialogs, drawers, tables).
     */
    attach: {
      type: [Boolean, String],
      default: false,
    },
    menuSearch: {
      type: Boolean,
      default: false,
    },
    menuContentClass: {
      type: String,
      default: '',
    },
    searchPlaceholder: {
      type: String,
      default: 'Search',
    },
    /** When false, menu opens above the field instead of below. */
    menuOffsetY: {
      type: Boolean,
      default: true,
    },
    /** Pin the open menu to the field and flip it when the row is near the footer. */
    flipMenu: {
      type: Boolean,
      default: false,
    },
    // required: {
    //   type: Boolean,
    //   default: false
    // }
  },
  methods: {
    collectScrollParents(node) {
      const parents = [];
      let el = node?.parentElement;
      while (el && el !== document.documentElement) {
        const style = window.getComputedStyle(el);
        const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;
        if (/(auto|scroll|overlay)/.test(overflow)) parents.push(el);
        el = el.parentElement;
      }
      return parents;
    },

    isScrollInsideMenu(event) {
      const target = event?.target;
      if (!target || target === document || target === window) return false;
      if (typeof target.closest !== 'function') return false;
      return !!(
        target.closest('.v-menu__content') ||
        target.closest('.v-select-list') ||
        target.closest('.lb-dropdown-menu')
      );
    },

    bindScrollClose() {
      if (this.scrollCloseBinding) return;
      const handler = (event) => {
        if (this.ignoreScrollClose) return;
        if (this.isScrollInsideMenu(event)) return;
        const el = this.$refs.element;
        if (el?.isMenuActive) this.forceCloseMenu();
      };
      const targets = this.collectScrollParents(this.$el);
      targets.push(window);
      targets.forEach((target) => {
        target.addEventListener('scroll', handler, { passive: true, capture: true });
      });
      this.scrollCloseBinding = { targets, handler };
    },

    suppressScrollClose(ms = 250) {
      this.ignoreScrollClose = true;
      if (this.scrollIgnoreTimer) window.clearTimeout(this.scrollIgnoreTimer);
      this.scrollIgnoreTimer = window.setTimeout(() => {
        this.ignoreScrollClose = false;
        this.scrollIgnoreTimer = null;
      }, ms);
    },

    unbindScrollClose() {
      if (!this.scrollCloseBinding) return;
      const { targets, handler } = this.scrollCloseBinding;
      (targets || []).forEach((target) => {
        target.removeEventListener('scroll', handler, { capture: true });
      });
      this.scrollCloseBinding = null;
    },

    setValue: function () {
      this.content = this.value;
    },
    onClear() {
      this.content = this.multiple ? [] : '';
      if (this.customvalue) {
        this.$emit('input', this.content);
        this.$emit('change', this.content);
      }
    },
    updateValue: function () {
      this.$emit("input", this.content);
      this.$emit("change", this.content);
      if (this.customvalue) {
        this.searchInput = null;
        this.closeMenuWithoutBlur();
        return;
      }
      if (!this.multiple) {
        this.forceCloseMenu();
      } else {
        this.searchInput = null;
      }
    },
    closeMenuWithoutBlur() {
      const el = this.$refs.element;
      if (!el) return;
      try {
        el.isMenuActive = false;
        if (el.$refs?.menu) {
          el.$refs.menu.isActive = false;
        }
      } catch (e) {
        /* ignore */
      }
    },
    /** Sync parent on input; close menu for single-select once a value is chosen. */
    onInternalInput: function (val) {
      if (this.multiple) return;
      if (this.customvalue) return;
      const selected = val !== undefined ? val : this.content;
      if (selected === undefined || selected === null || selected === "") return;
      this.forceCloseMenu();
    },
    /**
     * Vuetify autocomplete often re-opens the menu on the next tick after
     * clearing search-input. Force-close immediately and again after ticks.
     */
    forceCloseMenu: function () {
      if (this.closeMenuTimer) {
        clearTimeout(this.closeMenuTimer);
        this.closeMenuTimer = null;
      }
      this.searchInput = null;
      if (this.menuSearch) {
        this.menuClosing = true;
        this.menuSearchQuery = "";
      }
      const close = () => {
        const el = this.$refs.element;
        if (!el) return;
        try {
          el.isMenuActive = false;
          if (el.$refs && el.$refs.menu) {
            el.$refs.menu.isActive = false;
          }
          if (typeof el.blur === "function") {
            el.blur();
          }
        } catch (e) {
          /* ignore */
        }
      };
      close();
      this.$nextTick(() => {
        close();
        this.closeMenuTimer = setTimeout(() => {
          close();
          this.closeMenuTimer = setTimeout(() => {
            close();
            this.menuClosing = false;
          }, 50);
        }, 0);
      });
    },
    setFocus: function () {
      this.$nextTick(() => {
        this.$refs.element.$el.querySelector("input").focus();
      });
    },
    isInactive: function (item) {
      const text = typeof item === 'object' ? (item[this.itemtext] || '') : (item || '');
      return text.toString().includes('(Inactive)');
    },
    normalizeComparableValue: function (value) {
      if (value === undefined || value === null) return "";
      return String(value);
    },

    bindMenuCloseReset() {
      if (this.menuCloseUnwatch || (!this.menuSearch && !this.flipMenu)) return;
      this.menuCloseUnwatch = this.$watch(
        () => this.$refs.element?.isMenuActive,
        (active) => {
          if (active) {
            if (this.menuClosing) {
              const el = this.$refs.element;
              if (el) {
                el.isMenuActive = false;
                if (el.$refs?.menu) el.$refs.menu.isActive = false;
              }
              return;
            }
            this.menuSearchQuery = '';
            this.suppressScrollClose();
            this.$nextTick(() => {
              requestAnimationFrame(() => {
                if (this.menuClosing || !this.$refs.element?.isMenuActive) return;
                this.pinMenuToField();
                if (this.menuSearch) this.focusMenuSearchInput();
                if (this.menuPinTimer) window.clearTimeout(this.menuPinTimer);
                this.menuPinTimer = window.setTimeout(() => {
                  this.menuPinTimer = null;
                  if (this.menuClosing || !this.$refs.element?.isMenuActive) return;
                  this.pinMenuToField();
                }, 50);
              });
            });
          } else {
            this.menuSearchQuery = '';
          }
        },
      );
    },

    refreshOpenMenuPosition() {
      if (this.menuClosing) return;
      if (!this.menuSearch || !this.$refs.element?.isMenuActive) return;
      this.pinMenuToField();
      this.$nextTick(() => {
        if (this.menuClosing || !this.$refs.element?.isMenuActive) return;
        requestAnimationFrame(() => {
          if (this.menuClosing || !this.$refs.element?.isMenuActive) return;
          this.pinMenuToField();
          if (this.menuPinTimer) window.clearTimeout(this.menuPinTimer);
          this.menuPinTimer = window.setTimeout(() => {
            this.menuPinTimer = null;
            if (this.menuClosing || !this.$refs.element?.isMenuActive) return;
            this.pinMenuToField();
          }, 50);
        });
      });
    },

    pinMenuToField() {
      if ((!this.menuSearch && !this.flipMenu) || !this.$refs.element?.isMenuActive) return;

      const fieldRef = this.$refs.element;
      const activatorEl = fieldRef?.$el;
      if (!activatorEl || !this.menuContentClass) return;

      positionMenuNearActivator({
        activatorEl,
        menuClass: this.menuContentClass,
        fieldRef,
        maxMenuHeight: 320,
      });
    },

    onMenuSearchItemClick() {
      if (this.multiple) return;
      this.forceCloseMenu();
    },

    focusMenuSearchInput() {
      const fieldRef = this.$refs.menuSearchInput;
      const inputFromRef = fieldRef?.$refs?.input
        || fieldRef?.$el?.querySelector?.('input');
      if (inputFromRef) {
        inputFromRef.focus();
        return;
      }

      const scope = this.menuContentClass
        ? `.${this.menuContentClass.split(/\s+/)[0]}`
        : '.v-menu__content--active';
      const input = document.querySelector(
        `${scope} [data-testid="dropdown__menu-search-input"] input`,
      );
      input?.focus();
    },

    getItemLabel(item) {
      if (item == null) return '';
      if (typeof item === 'object') {
        return item[this.itemtext] || item[this.itemvalue] || '';
      }
      return String(item);
    },

    itemMatchesSearch(item) {
      const query = (this.menuSearchQuery || '').trim().toLowerCase();
      if (!query) return true;
      return this.getItemLabel(item).toLowerCase().includes(query);
    },

    menuFilter() {
      return true;
    },
  },
  beforeDestroy() {
    this.unbindScrollClose();
    this.menuCloseUnwatch?.();
    this.menuCloseUnwatch = null;
    if (this.closeMenuTimer) {
      clearTimeout(this.closeMenuTimer);
      this.closeMenuTimer = null;
    }
    if (this.menuPinTimer) {
      window.clearTimeout(this.menuPinTimer);
      this.menuPinTimer = null;
    }
    if (this.scrollIgnoreTimer) {
      window.clearTimeout(this.scrollIgnoreTimer);
      this.scrollIgnoreTimer = null;
    }
  },
  computed: {
    usesPrimitiveItems() {
      const items = this.items || [];
      return items.length > 0 && typeof items[0] !== 'object';
    },
    effectiveItemText() {
      if (this.usesPrimitiveItems) {
        return (item) => (item == null ? '' : String(item));
      }
      return this.itemtext;
    },
    effectiveItemValue() {
      if (this.usesPrimitiveItems) {
        return (item) => item;
      }
      return this.itemvalue;
    },
    /** Prefer appendiconinner (app convention), else appendicon. */
    resolvedAppendIcon() {
      return this.appendiconinner || this.appendicon || "";
    },
    menuProps() {
      const contentClass = ['lb-dropdown-menu', this.menuContentClass]
        .filter(Boolean)
        .join(' ');

      const base = {
        contentClass: this.itemClass ? `${this.itemClass} ${contentClass}` : contentClass,
        maxHeight: 320,
        closeOnClick: true,
      };

      if (this.menuSearch || this.flipMenu) {
        return {
          ...base,
          attach: false,
          fixed: true,
          offsetY: this.menuOffsetY,
          zIndex: DIALOG_MENU_Z_INDEX,
          closeOnContentClick: this.flipMenu ? !this.multiple : false,
        };
      }

      return {
        ...base,
        attach: this.attach,
        offsetY: this.menuOffsetY,
        closeOnContentClick: !this.multiple,
      };
    },

    resolvedItems() {
      if (!this.menuSearch) return this.items;

      const query = (this.menuSearchQuery || '').trim().toLowerCase();
      if (!query) return this.items;

      return (this.items || []).filter((item) => {
        const label = this.getItemLabel(item).toLowerCase();
        return label.includes(query);
      });
    },
  },
  watch: {
    value: function () {
      this.$nextTick(() => {
        this.setValue();
      });
    },
    menuSearchQuery() {
      this.refreshOpenMenuPosition();
    },
    content: function (val, oldVal) {
      if (this.multiple) {
        if (this.menuSearch) this.refreshOpenMenuPosition();
        return;
      }
      if (this.customvalue) return;
      if (val === oldVal) return;
      if (val !== undefined && val !== null && val !== "") {
        this.forceCloseMenu();
        return;
      }
      if (this.menuSearch) this.refreshOpenMenuPosition();
    },
    items: function (newItems) {
      let hasitem = false;
      const isMultipleValue = this.multiple || Array.isArray(this.content);
      const selectedValues = isMultipleValue
        ? (Array.isArray(this.content) ? this.content : [])
            .map((value) => this.normalizeComparableValue(value))
            .filter((value) => value !== "")
        : [this.normalizeComparableValue(this.content)].filter((value) => value !== "");

      if (newItems.length > 0) {
        if (
          typeof newItems[0] === "object" &&
          !(newItems[0] instanceof Array)
        ) {
          const itemValues = newItems.map((item) =>
            this.normalizeComparableValue(item[this.itemvalue])
          );
          hasitem = isMultipleValue
            ? selectedValues.every((value) => itemValues.includes(value))
            : itemValues.includes(this.normalizeComparableValue(this.content));
        } else {
          const itemValues = newItems.map((item) => this.normalizeComparableValue(item));
          hasitem = isMultipleValue
            ? selectedValues.every((value) => itemValues.includes(value))
            : itemValues.includes(this.normalizeComparableValue(this.content));
        }
      }
      if (!hasitem && !this.customvalue) this.content = isMultipleValue ? [] : "";
      if (!isMultipleValue && newItems.length === 1 && (this.content || "").toString() === "") {
        if (newItems[0] instanceof Array)
          this.content = (newItems[0] || this.content || "").toString();
        else if (typeof newItems[0] === "object")
          this.content = (newItems[0][this.itemvalue] || "").toString();
        this.updateValue();
      }
    },
  },
};
</script>

<style>
.lb-dropdown-menu {
  border-radius: var(--radius-8) !important;
  margin-left: 0 !important;
}

.dependency-link-type-menu .v-list-item--disabled {
  display: flex !important;
  opacity: 1;
  pointer-events: none;
}

.dependency-link-type-menu .v-list-item--disabled .v-list-item__title,
.dependency-link-type-menu .v-list-item--disabled .v-list-item__content {
  color: var(--grey-400, #9ca3af) !important;
}

/* Keep outlined dense fields consistent; does not change colours/layout of callers */
.autocomplete-dropdown.v-text-field--outlined.v-input--dense .v-input__slot {
  min-height: 2rem;
}

.autocomplete-dropdown.v-text-field--outlined fieldset {
  border-radius: var(--radius-sm, 8px);
}

.autocomplete-dropdown .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center !important;
}

.v-list-item__action {
  margin-right: 8px !important;
}

.lb-dropdown__menu-search-icon {
  width: var(--icon-xs, 16px);
  height: var(--icon-xs, 16px);
  display: block;
}

.lb-dropdown__menu-search .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center !important;
  padding-left: var(--space-xs, 8px);
}

.lb-dropdown__selected-chip {
  display: inline-flex !important;
  align-items: center !important;
  flex: 0 0 auto !important;
  max-width: 260px;
  height: 1.5rem !important;
  min-height: 1.5rem !important;
  overflow: visible !important;
}

.lb-dropdown__selected-chip-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 4px;
}

.lb-dropdown__selected-chip-close {
  flex-shrink: 0 !important;
  font-size: var(--font-size-sm) !important;
  cursor: pointer;
}
</style>